import React, { useState, useRef, useEffect } from 'react';
import { clsx } from 'clsx';

interface DropdownProps {
  children: React.ReactNode;
  overlay: React.ReactNode;
  trigger?: 'click' | 'hover';
  placement?: 'bottom' | 'bottomLeft' | 'bottomRight' | 'top' | 'topLeft' | 'topRight';
  className?: string;
}

const Dropdown: React.FC<DropdownProps> = ({
  children,
  overlay,
  trigger = 'click',
  placement = 'bottom',
  className
}) => {
  const [isOpen, setIsOpen] = useState(false);
  const dropdownRef = useRef<HTMLDivElement>(null);

  const placementClasses = {
    bottom: 'top-full left-1/2 transform -translate-x-1/2 mt-1',
    bottomLeft: 'top-full left-0 mt-1',
    bottomRight: 'top-full right-0 mt-1',
    top: 'bottom-full left-1/2 transform -translate-x-1/2 mb-1',
    topLeft: 'bottom-full left-0 mb-1',
    topRight: 'bottom-full right-0 mb-1'
  };

  useEffect(() => {
    const handleClickOutside = (event: MouseEvent) => {
      if (dropdownRef.current && !dropdownRef.current.contains(event.target as Node)) {
        setIsOpen(false);
      }
    };

    if (isOpen) {
      document.addEventListener('mousedown', handleClickOutside);
    }

    return () => {
      document.removeEventListener('mousedown', handleClickOutside);
    };
  }, [isOpen]);

  const handleTrigger = () => {
    if (trigger === 'click') {
      setIsOpen(!isOpen);
    }
  };

  const handleMouseEnter = () => {
    if (trigger === 'hover') {
      setIsOpen(true);
    }
  };

  const handleMouseLeave = () => {
    if (trigger === 'hover') {
      setIsOpen(false);
    }
  };

  return (
    <div 
      ref={dropdownRef}
      className={clsx('relative inline-block', className)}
      onMouseEnter={handleMouseEnter}
      onMouseLeave={handleMouseLeave}
    >
      <div onClick={handleTrigger}>
        {children}
      </div>
      
      {isOpen && (
        <div
          className={clsx(
            'absolute z-50 min-w-[160px] bg-white border border-gray-200 rounded-lg shadow-lg py-1',
            placementClasses[placement]
          )}
        >
          {overlay}
        </div>
      )}
    </div>
  );
};

export default Dropdown; 